import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FolderKanban, Library, ArrowUpRight, Inbox, LineChart, LifeBuoy, Camera, Clock, Sparkles, BookText, CalendarClock, AlertTriangle } from "lucide-react";
import api from "@/lib/api";

const Mono = ({ children, className = "" }) => (
  <span className={`font-mono uppercase tracking-[0.2em] ${className}`}>{children}</span>
);

const DL_STATUS = {
  overdue: { label: "Overdue", cls: "border-red-500/50 bg-red-500/10 text-red-300" },
  due_soon: { label: "Due soon", cls: "border-amber-400/50 bg-amber-400/10 text-amber-300" },
  upcoming: { label: "Upcoming", cls: "border-gold-500/30 bg-navy-900/40 text-white/60" },
};

export default function Overview() {
  const [stats, setStats] = useState(null);
  const [deadlines, setDeadlines] = useState(null);
  const nav = useNavigate();

  useEffect(() => {
    api.get("/dashboard/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/fieldclaim/programs/upcoming?limit=6").then((r) => setDeadlines(r.data)).catch(() => {});
  }, []);

  const cells = [
    { ref: "PRJ", label: "Projects", value: stats?.projects ?? "—", to: "/dashboard/projects", icon: FolderKanban },
    { ref: "PGM", label: "Portfolio", value: "Open", to: "/dashboard/portfolio", icon: LineChart },
    { ref: "DRF", label: "RAAgent", value: "Open", to: "/dashboard/raagent", icon: LifeBuoy },
    { ref: "AIX", label: "AI Expert", value: "Open", to: "/dashboard/expert", icon: Sparkles },
  ];

  return (
    <div className="space-y-10 text-white" data-testid="overview-page">
      <div>
        <Mono className="text-gold-400 text-[0.65rem]">Sheet 00 — Overview</Mono>
        <h1 className="font-heading font-light text-4xl tracking-tight mt-3">Your recovery programs at a glance</h1>
      </div>

      {/* title-block stat row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 border-t border-l border-gold-500/25">
        {cells.map((c) => (
          <button
            key={c.label}
            onClick={() => nav(c.to)}
            data-testid={`stat-${c.label.toLowerCase().replace(/\s/g, "-")}`}
            className="group text-left border-r border-b border-gold-500/25 bg-navy-900/40 p-6 hover:bg-navy-800 transition-colors"
          >
            <div className="flex items-center justify-between mb-5">
              <Mono className="text-[0.55rem] text-gold-500/70">{c.ref}</Mono>
              <c.icon className="h-4 w-4 text-gold-500/70 group-hover:text-gold-400 transition-colors" />
            </div>
            <div className="font-mono text-4xl text-white">{c.value}</div>
            <Mono className="text-white/40 mt-2 text-[0.55rem] block">{c.label}</Mono>
          </button>
        ))}
      </div>

      {/* DRFA deadlines across events */}
      {deadlines && deadlines.deadlines.length > 0 && (
        <div data-testid="overview-deadlines">
          <div className="flex items-center justify-between mb-4">
            <Mono className="text-gold-400 text-[0.6rem] flex items-center gap-2">
              <CalendarClock className="h-3.5 w-3.5" /> Next DRFA deadlines — all events
            </Mono>
            <div className="flex items-center gap-2">
              {deadlines.counts.overdue > 0 && (
                <span className="inline-flex items-center gap-1 border border-red-500/50 bg-red-500/15 text-red-300 text-[0.6rem] font-mono px-2 py-0.5 rounded-sm" data-testid="overview-overdue-count">
                  <AlertTriangle className="h-3 w-3" /> {deadlines.counts.overdue} overdue
                </span>
              )}
              <button onClick={() => nav("/dashboard/drfa")} data-testid="overview-all-programs" className="text-gold-400 hover:text-gold-300 text-xs font-light inline-flex items-center gap-1">
                All programs <ArrowUpRight className="h-3 w-3" />
              </button>
            </div>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {deadlines.deadlines.map((d) => (
              <button key={`${d.program_id}-${d.key}`} onClick={() => nav(`/dashboard/drfa/${d.program_id}`)}
                data-testid={`deadline-tile-${d.program_id}-${d.key}`}
                className={`group text-left border p-4 transition-colors hover:bg-navy-800 ${DL_STATUS[d.status].cls}`}>
                <div className="flex items-center justify-between gap-2">
                  <Mono className="text-[0.5rem] opacity-80">{d.agrn || d.program_name}</Mono>
                  <span className="font-mono text-[0.6rem] uppercase tracking-widest shrink-0">
                    {d.days_left < 0 ? `${Math.abs(d.days_left)}d over` : `${d.days_left}d left`}
                  </span>
                </div>
                <div className="text-white text-sm mt-2 leading-snug">{d.title}</div>
                <div className="flex items-center justify-between mt-2.5">
                  <span className="font-mono text-xs opacity-90">{new Date(d.due + "T00:00:00").toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" })}</span>
                  <Mono className="text-[0.45rem] opacity-70">{DL_STATUS[d.status].label}</Mono>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* MDAu Suite */}
      <div>
        <Mono className="text-gold-400 text-[0.6rem] block mb-4">MDAu Suite</Mono>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="mdau-suite">
          {[
            { name: "FieldClaim", ref: "APP-01", desc: "Audit-ready DRFA field evidence capture.", to: "/dashboard/fieldclaim", status: "live", icon: Camera },
            { name: "RAAgent", ref: "APP-02", desc: "DRFA / SmartyGrants submission builder and evaluator.", to: "/dashboard/raagent", status: "live", icon: LifeBuoy },
            { name: "AI Expert", ref: "APP-03", desc: "Grounded answers & quick wins from your document library.", to: "/dashboard/expert", status: "live", icon: Sparkles },
            { name: "Document Library", ref: "APP-04", desc: "NSWRA / NDP references plus your council documents.", to: "/dashboard/library", status: "live", icon: BookText },
            { name: "TimeCapture", ref: "APP-05", desc: "Crew hours self-recorded, allocated across assets as actuals.", to: "/dashboard/timecapture", status: "live", icon: Clock },
            { name: "DRFA Programs", ref: "APP-06", desc: "Every NSWRA deadline pushed out from the event date.", to: "/dashboard/drfa", status: "live", icon: CalendarClock },
          ].map((app) => {
            const Inner = (
              <>
                <div className="flex items-center justify-between mb-5">
                  <Mono className="text-[0.5rem] text-gold-500/60">{app.ref}</Mono>
                  <app.icon className="h-4 w-4 text-gold-500/70" />
                </div>
                <div className="font-heading text-lg text-white">{app.name}</div>
                <p className="text-white/45 text-xs mt-1.5 font-light leading-relaxed">{app.desc}</p>
                <div className="mt-4">
                  {app.status === "live" ? (
                    <Mono className="text-[0.5rem] text-emerald-400/80">● Live</Mono>
                  ) : (
                    <Mono className="text-[0.5rem] text-white/35">Coming soon</Mono>
                  )}
                </div>
              </>
            );
            const cls = `text-left border border-gold-500/20 bg-navy-900/40 p-5 transition-colors ${app.status === "soon" ? "opacity-55 cursor-default" : "hover:bg-navy-800 hover:border-gold-500/40"}`;
            if (app.status === "soon") return <div key={app.name} data-testid={`suite-${app.name.toLowerCase().replace(/\s/g, "-")}`} className={cls}>{Inner}</div>;
            if (app.href) return <a key={app.name} href={app.href} target="_blank" rel="noreferrer" data-testid={`suite-${app.name.toLowerCase().replace(/\s/g, "-")}`} className={`group block ${cls}`}>{Inner}</a>;
            return <button key={app.name} onClick={() => nav(app.to)} data-testid={`suite-${app.name.toLowerCase().replace(/\s/g, "-")}`} className={`group block ${cls}`}>{Inner}</button>;
          })}
        </div>
      </div>

      {/* action rows */}
      <div>
        <Mono className="text-gold-400 text-[0.6rem] block mb-4">Start Here</Mono>
        <div className="border-t border-gold-500/25">
          {[
            { icon: LineChart, ref: "A-01", title: "Manage your program & portfolio", body: "Budgets, health status, cash-flow exposure and critical path — with AI executive reporting for clients.", to: "/dashboard/portfolio", testid: "action-portfolio" },
            { icon: LifeBuoy, ref: "A-02", title: "Build & QA a DRFA / SmartyGrants submission", body: "Assemble MARS-ready claims with automated QA and assessor-grade evaluation so they can't be denied.", to: "/dashboard/raagent", testid: "action-raagent" },
            { icon: Sparkles, ref: "A-03", title: "Ask the AI Expert", body: "Grounded answers and one-click quick wins from your NSWRA / NDP document library.", to: "/dashboard/expert", testid: "action-expert" },
            { icon: Library, ref: "A-04", title: "Browse people & systems", body: "Find vetted consultants and ready-to-deploy frameworks.", to: "/dashboard/resources", testid: "action-resources" },
          ].map((a) => (
            <button
              key={a.ref}
              onClick={() => nav(a.to)}
              data-testid={a.testid}
              className="group w-full text-left grid grid-cols-[60px_auto_1fr_auto] items-center gap-5 py-6 border-b border-gold-500/15 hover:bg-gold-500 transition-colors"
            >
              <Mono className="text-gold-400 text-sm group-hover:text-navy-950 transition-colors">{a.ref}</Mono>
              <a.icon className="h-5 w-5 text-gold-500/80 group-hover:text-navy-950 transition-colors" />
              <div>
                <div className="font-heading text-xl group-hover:text-navy-950 transition-colors">{a.title}</div>
                <div className="text-sm text-white/50 group-hover:text-navy-900/70 transition-colors font-light">{a.body}</div>
              </div>
              <ArrowUpRight className="h-5 w-5 text-white/30 group-hover:text-navy-950 group-hover:translate-x-1 group-hover:-translate-y-1 transition-all justify-self-end" />
            </button>
          ))}
        </div>
      </div>

      {stats?.is_admin && (
        <div className="border border-gold-500/30 bg-navy-900/40 p-6 flex items-center gap-4" data-testid="admin-leads-banner">
          <Inbox className="h-6 w-6 text-gold-400" />
          <div>
            <div className="font-heading text-lg">{stats.leads} contact leads received</div>
            <div className="text-white/50 text-sm font-light">Admin view — captured from the public contact form.</div>
          </div>
        </div>
      )}
    </div>
  );
}
